<template>
  <div class="pt-1 pr-1 pl-1">
    <div class="no-gutters mt-1 mb-3 ml-3 mr-3">
      <div class="row">
        <div class="col">
          <div class="parameter-box">
            <h5 v-b-popover.hover.bottom="{ content: latitude + ', ' + longitude, html: true }">
              {{ position }}
              <b-button v-on:click="copyCoordinates" variant="outline-secondary" class="ml-1" size="xs" title="Copy decimal coordinates to clipboard">
                <font-awesome-icon :icon="['fas', 'copy']"></font-awesome-icon>
              </b-button>
            </h5>
            <div class="parameter-name">Current position</div>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-7">
          <div class="parameter-box">
            <h4 v-if="speed !== null">
              <a href="#" class="parameter-link" @click="selectParameter(1, 6)" title="Show speed in chart and map">{{ showDigits(roundDigits(speed, 1), 1) }}</a> <small>m/s</small>
            </h4>
            <h4 v-else class="text-muted"><small>n/a</small></h4>
            <div class="parameter-name">Speed</div>
          </div>
        </div>
        <div class="col-5">
          <div class="parameter-box">
            <h4 v-if="heading !== null">
              <a href="#" class="parameter-link" @click="selectParameter(1, 3)" title="Show heading in chart and map">{{ roundDigits(heading, 1) }}</a><small>&deg;</small>
              <font-awesome-icon :icon="['fas', 'location-arrow']" size="xs" class="text-info ml-2" :style="{ transform: rotateArrow(heading) }" />
            </h4>
            <h4 v-else class="text-muted"><small>n/a</small></h4>
            <div class="parameter-name">Heading</div>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-7">
          <div class="parameter-box">
            <h4 v-if="depth !== null">
              <a href="#" class="parameter-link" @click="selectParameter(1, 4)" title="Show depth in chart and map">{{ showDigits(roundDigits(depth, 1), 1) }}</a> <small>m</small>
              <font-awesome-icon v-if="velocity_z < -0.1" size="sm" class="ml-1 align-baseline" :icon="['fas', 'long-arrow-alt-up']" />
              <font-awesome-icon v-if="velocity_z > 0.1" size="sm" class="ml-1 align-baseline" :icon="['fas', 'long-arrow-alt-down']" />
              <span v-if="Math.abs(velocity_z) > 0.1" class="text-muted align-middle velocity-z">
                <a href="#" class="parameter-link" @click="selectParameter(1, 7)" title="Show velocity z in chart and map">{{ roundDigits(Math.abs(velocity_z), 1) }}</a> <small>m/s</small>
              </span>
            </h4>
            <h4 v-else class="text-muted"><small>n/a</small></h4>
            <div class="parameter-name">
              Depth <font-awesome-icon :icon="['fas', 'circle']" size="xs" class="text-primary align-baseline" />
            </div>
          </div>
        </div>
        <div class="col-5">
          <div class="parameter-box">
            <h4 v-if="altitude !== null">
              <a href="#" class="parameter-link" @click="selectParameter(1, 5)" title="Show altitude in chart and map"><span :class="altitude_class">{{ showDigits(roundDigits(altitude, 1), 1) }}</span></a> <small>m</small>
              <small><span class="text-muted ml-1" style="font-size: 11px">({{ dvl_data_age }} s ago)</span></small>
              <!-- TODO: computed property with customized minimum altitude -->
              <!-- <font-awesome-icon class="text-warning ml-2 align-baseline" size="xs" v-if="altitude < 0.8" :icon="['fas', 'exclamation-triangle']" />-->
            </h4>
            <h4 v-else class="text-muted"><small>n/a</small></h4>
            <div class="parameter-name">
              Altitude <font-awesome-icon :icon="['fas', 'circle']" size="xs" class="text-danger align-baseline" />
            </div>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-7">
          <div class="parameter-box">
            <h4 v-if="step !== null && total_steps !== null">
              <a href="#" class="parameter-link" @click="selectParameter(4, 2)" title="Show current step in chart and map">{{ step }}</a> /
              <a href="#" class="parameter-link" @click="selectParameter(4, 3)" title="Show total steps in chart and map">{{ total_steps }}</a>
            </h4>
            <h4 v-else class="text-muted"><small>n/a</small></h4>
            <div class="parameter-name">Current step</div>
          </div>
        </div>
        <div class="col-5">
          <div class="parameter-box">
            <h4 v-if="battery !== null">
              <a href="#" class="parameter-link" @click="selectParameter(3, 9)" title="Show battery in chart and map">{{ battery }}</a><small>%</small>
              <font-awesome-icon class="text-success ml-1 align-baseline" rotation="270" size="xs" v-if="battery > 75" :icon="['fas', 'battery-full']" />
              <font-awesome-icon class="text-success ml-1 align-baseline" rotation="270" size="xs" v-else-if="battery > 50 && battery <= 75" :icon="['fas', 'battery-three-quarters']" />
              <font-awesome-icon class="text-warning ml-1 align-baseline" rotation="270" size="xs" v-else-if="battery > 25 && battery <= 50" :icon="['fas', 'battery-half']" />
              <font-awesome-icon class="text-warning ml-1 align-baseline" rotation="270" size="xs" v-else-if="battery > 10 && battery <= 25" :icon="['fas', 'battery-quarter']" />
              <font-awesome-icon class="text-danger ml-1 align-baseline" rotation="270" size="xs" v-else-if="battery < 10" :icon="['fas', 'battery-empty']" />
            </h4>
            <h4 v-else class="text-muted"><small>n/a</small></h4>
            <div class="parameter-name">Battery charge</div>
          </div>
        </div>
      </div>
      <div class="no-gutters mt-2 ml-2 mr-2">
        <div class="row">
          <div class="col">
            <hr class="p-0 mt-2 mb-2">
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <div class="state-box" :class="getTimerStyle(global_timer)">
              Global timer ({{ global_timer }} secs)
            </div>
          </div>
          <div class="col-6">
            <div class="state-box" :class="getThrusterStyle(thrusters_enabled)">
              Thrusters <span v-if="thrusters_enabled">enabled</span><span v-else>disabled</span>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col">
            <hr class="p-0 mt-2 mb-2">
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <div class="state-box" :class="getStateStyle(abort_and_surface)">
              Abort and surface
            </div>
          </div>
          <div class="col-6">
            <div class="state-box" :class="getStateStyle(emergency_surface)">
              Emergency surface
            </div>
          </div>
        </div>
        <div class="row mt-1">
          <div class="col-6">
            <div class="state-box" :class="getStateStyle(water_detected)">
              Water detected
            </div>
          </div>
          <div class="col-6">
            <div class="state-box" :class="getStateStyle(low_battery)">
              Low battery
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex'
import Helpers from '@/mixins/helpers'

const GLOBAL_TIMER_MIN_SEC = 300

export default {
  name: 'AuvStatusWidget',
  mixins: [
    Helpers
  ],
  components: {
  },
  data: function () {
    return {
      spStyles: {
      },
      spDepthData: (() => {
        return []
      })(),
      spLineStylesDepth: {
        stroke: '#007bff'
      },
      spAltitudeData: (() => {
        return []
      })(),
      spLineStylesAltitude: {
        stroke: '#dc3545'
      },
      spMinWaterDepth: 0,
      spMaxWaterDepth: 0,
      spDepthDataSize: 30
    }
  },
  mounted () {
    this.$nextTick(() => {
      setInterval(this.updateDepthAltitudeLines, 1000)
    })
  },
  props: ['platformId'],
  methods: {
    ...mapMutations(['select_parameter']),
    selectParameter: function (entityId, parameterId) {
      this.select_parameter([this.platformId, entityId, parameterId])
    },
    copyCoordinates: function () {
      this.$copyText(this.latitude + ', ' + this.longitude).then(function (e) {
        // No user response intended
      }, function (e) {
        alert('auv dashboard widget: Error while trying to copy coordinates')
      })
    },
    updateDepthAltitudeLines: function () {
      if (this.depth != null) {
        if (this.spDepthData.length > this.spDepthDataSize) {
          this.spDepthData.shift()
        }
        this.spDepthData.push(-this.depth)
      }
      if (this.altitude != null) {
        if (this.spAltitudeData.length > this.spDepthDataSize) {
          this.spAltitudeData.shift()
        }
        this.spAltitudeData.push(-(this.depth + this.altitude))
      }
      this.spMinWaterDepth = Math.min.apply(Math, this.spDepthData) + 1.0
      this.spMaxWaterDepth = Math.min.apply(Math, this.spAltitudeData)
    },
    getThrusterStyle: function (state) {
      if (state === true) {
        return 'state-ok'
      } else {
        return 'state-warning'
      }
    },
    getTimerStyle: function (timer) {
      if (timer === 0) {
        return 'state-danger'
      } else if (timer <= GLOBAL_TIMER_MIN_SEC) {
        return 'state-warning'
      } else {
        return 'state-ok'
      }
    },
    getStateStyle: function (state) {
      if (state === true) {
        return 'state-danger'
      } else {
        return 'state-ok'
      }
    },
    rotateArrow: function (heading) {
      return 'rotate(' + Number(Number(heading) - 45) + 'deg)'
    },
    getParameterDiff: function (entityId, parameterId) {
      const current = this.currentUpdate(this.platformId, entityId, parameterId).value
      const last = this.lastUpdate(this.platformId, entityId, parameterId).value
      if (current > last) {
        return 1
      } else if (current < last) {
        return -1
      } else {
        return 0
      }
    }
  },
  computed: {
    ...mapState(['platforms']),
    ...mapGetters(['lastUpdate', 'currentUpdate']),
    position () {
      const position = this.getCoordinatesNautical(this.roundDigits(this.latitude, 5),
        this.roundDigits(this.longitude, 5))
      return position.lat + ' ' + position.lng
    },
    latitude () {
      const update = this.currentUpdate(this.platformId, 1, 1)
      return (update.length !== 0) ? update[1] : 0
    },
    longitude () {
      const update = this.currentUpdate(this.platformId, 1, 2)
      return (update.length !== 0) ? update[1] : 0
    },
    heading () {
      const update = this.currentUpdate(this.platformId, 1, 3)
      return (update.length !== 0) ? Number(update[1]) : 0
    },
    depth () {
      const update = this.currentUpdate(this.platformId, 1, 4)
      return (update.length !== 0) ? Number(update[1]) : 0
    },
    altitude () {
      const update = this.currentUpdate(this.platformId, 1, 5)
      return (update.length !== 0) ? Number(update[1]) : 0
    },
    altitude_class () {
      const update = this.currentUpdate(this.platformId, 1, 5)
      if (update.length !== 0) {
        if (String(update[1]) === 'True') {
          return 'text-danger'
        } else if (String(update[1]) !== 'False' && Number(update[1]) < 0.8) { // FIXME: Magic number
          return 'text-warning'
        }
      }
      return ''
    },
    speed () {
      const update = this.currentUpdate(this.platformId, 1, 6)
      return (update.length !== 0) ? Number(update[1]) : 0
    },
    velocity_z () {
      const update = this.currentUpdate(this.platformId, 1, 7)
      return (update.length !== 0) ? Number(update[1]) : 0
    },
    battery () {
      const update = this.currentUpdate(this.platformId, 3, 9)
      return (update.length !== 0) ? Number(update[1]) : 0
    },
    step () {
      const update = this.currentUpdate(this.platformId, 4, 2)
      return (update.length !== 0) ? Number(update[1]) : 0
    },
    total_steps () {
      const update = this.currentUpdate(this.platformId, 4, 3)
      return (update.length !== 0) ? Number(update[1]) : 0
    },
    emergency_surface () {
      const update = this.currentUpdate(this.platformId, 2, 1)
      return (update.length !== 0) ? (String(update[1]).toLowerCase() === 'true') : false
    },
    water_detected () {
      const update = this.currentUpdate(this.platformId, 2, 3)
      return (update.length !== 0) ? (String(update[1]).toLowerCase() === 'true') : false
    },
    abort_and_surface () {
      const update = this.currentUpdate(this.platformId, 2, 4)
      return (update.length !== 0) ? (String(update[1]).toLowerCase() === 'true') : false
    },
    low_battery () {
      const update = this.currentUpdate(this.platformId, 3, 2)
      return (update.length !== 0) ? (String(update[1]).toLowerCase() === 'true') : false
    },
    global_timer () {
      const update = this.currentUpdate(this.platformId, 2, 5)
      return (update.length !== 0) ? Number(update[1]) : 0
    },
    thrusters_enabled () {
      const update = this.currentUpdate(this.platformId, 2, 6)
      if (update.length !== 0) {
        if (Number(update[1]) === 1) {
          return true
        }
      }
      return false
    }
  }
}
</script>

<style lang="scss" scoped>
.parameter-box {
  padding: 10px 0 0 10px;
}
.parameter-box h4 {
  margin-bottom: 2px;
}
.parameter-box h5 {
  margin-bottom: 2px;
}
.parameter-name {
  font-size: 11px;
  color: #6c757d;
}
.parameter-link {
  color: #212529;
  text-decoration: none;
}
.state-box {
  font-size: 12px;
  font-weight: bold;
  text-align: center;
  vertical-align: bottom;
  border-radius: 2px;
  padding: 5px 2px 5px 2px;
}
.state-muted {
  background-color: rgba(0,0,0,.05);
  color: #6c757d;
}
.state-ok {
  background-color: #82E0AA;
  color: #186A3B;
}
.state-danger {
  background-color: #F1948A;
  color: #78281F;
}
.state-warning {
  background-color: #ffc107;
  color: #755905;
}
.no-gutters {
  margin-right: 10px;
  margin-left: 10px;
  .col, [class*="col-"] {
    padding-right: 2px;
    padding-left: 2px;
  }
}
.btn-xs {
  padding: .25rem .4rem;
  font-size: .875rem;
  line-height: .5;
  border-radius: .2rem;
}
.velocity-z {
  font-size: 15px;
}
</style>
